import type { ReactNode } from 'react';
import { Rocket } from 'lucide-react';
import { findMarket, isSiteOrderActive } from '@cp/shared';
import { Button, Card, Flag, StatusPill } from '@/components/ui';
import { useRecentSiteOrders } from '@/hooks/useSiteOrders';
import { SITE_ORDER_STATE } from '@/utils/site-orders';
import type { SiteOrderSummary } from '@/types/api';

const DAY_MS = 24 * 60 * 60_000;
const HALF_HOUR_MS = 30 * 60_000;

/** Under way; failed in the last day, to see why; done in the last half hour, to open. */
function worthShowing(order: SiteOrderSummary, now: number): boolean {
  if (isSiteOrderActive(order.state)) return true;
  const finished = order.finishedAt ? now - new Date(order.finishedAt).getTime() : Infinity;
  return order.state === 'failed' ? finished < DAY_MS : finished < HALF_HOUR_MS;
}

/**
 * Sites being made (ADR-0059), above the list: each with where it is, and
 * the window that follows it one click away — the wizard can be closed
 * while a site is made, and nothing is lost.
 */
export function SiteOrdersStrip({ onOpen }: { readonly onOpen: (orderId: string) => void }): ReactNode {
  const orders = useRecentSiteOrders();
  const now = Date.now();
  const shown = (orders.data ?? []).filter((order) => worthShowing(order, now));
  if (shown.length === 0) return null;

  return (
    <Card title="В работе" icon={Rocket}>
      <ul className="list-plain stack stack-tight">
        {shown.map((order) => {
          const market = findMarket(order.market);
          return (
            <li key={order.id} className="row-between row-wrap">
              <span className="row-tight">
                <Flag country={market?.country ?? null} language={market?.language ?? null} />
                <span className="strong">{order.displayName}</span>
                <span className="mono muted">{order.domain}</span>
              </span>
              <span className="row-tight">
                <StatusPill map={SITE_ORDER_STATE} status={order.state} />
                <Button size="sm" variant={isSiteOrderActive(order.state) ? 'primary' : 'default'} onClick={() => onOpen(order.id)}>
                  {isSiteOrderActive(order.state) ? 'Следить' : 'Открыть'}
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
